import { useState, useEffect, useCallback, useRef } from 'react';
import { apiUrl } from '../../config';
import { useAuthStore } from '../../stores/authStore';
import './FrameGalleryOverlay.css';

interface GalleryFrame {
  index: number;
  standard: string | null;
  hd: string | null;
  '4k': string | null;
}

type Resolution = 'standard' | 'hd' | '4k';

interface Props {
  shopId: string;
  sectionId: string;
  sectionName: string;
  onClose: () => void;
}

function getTouchDist(t1: React.Touch, t2: React.Touch) {
  const dx = t1.clientX - t2.clientX;
  const dy = t1.clientY - t2.clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

export default function FrameGalleryOverlay({ shopId, sectionId, sectionName, onClose }: Props) {
  const token = useAuthStore((s) => s.token);
  const [frames, setFrames] = useState<GalleryFrame[]>([]);
  const [loading, setLoading] = useState(true);
  const [baseUrl, setBaseUrl] = useState('');
  const [sasToken, setSasToken] = useState('');

  // Detail mode
  const [detailIndex, setDetailIndex] = useState<number | null>(null);
  const [resolution, setResolution] = useState<Resolution>('standard');

  // Zoom + pan (stored in refs for smooth touch, synced to state for render)
  const scaleRef = useRef(1);
  const txRef = useRef(0); // translation x
  const tyRef = useRef(0); // translation y
  const [transform, setTransform] = useState({ scale: 1, tx: 0, ty: 0 });

  // Touch gesture tracking
  const pinching = useRef(false);
  const pinchStartDist = useRef(0);
  const pinchStartScale = useRef(1);
  const pinchMidX = useRef(0);
  const pinchMidY = useRef(0);
  const panStartTouchX = useRef(0);
  const panStartTouchY = useRef(0);
  const panStartTx = useRef(0);
  const panStartTy = useRef(0);
  const lastTapTime = useRef(0);

  function applyTransform(scale: number, tx: number, ty: number) {
    scaleRef.current = scale;
    txRef.current = tx;
    tyRef.current = ty;
    setTransform({ scale, tx, ty });
  }

  function resetZoom() {
    applyTransform(1, 0, 0);
  }

  function goToDetail(index: number) {
    setDetailIndex(index);
    setResolution('standard');
    resetZoom();
  }

  // Fetch gallery data
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(apiUrl(`/api/shops/${shopId}/sections/${sectionId}/gallery`), { headers });
        if (!res.ok) throw new Error('Failed to load');
        const data = await res.json();
        if (cancelled) return;
        setFrames(data.frames || []);
        setBaseUrl(data.base_url || '');
        setSasToken(data.sas_token || '');
      } catch { /* ignore */ }
      if (!cancelled) setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [shopId, sectionId, token]);

  const frameUrl = useCallback((blobName: string | null) => {
    if (!blobName || !baseUrl || !sasToken) return '';
    return `${baseUrl}/${blobName}?${sasToken}`;
  }, [baseUrl, sasToken]);

  const resolvedUrl = useCallback((frame: GalleryFrame, res: Resolution): string => {
    if (res === '4k' && frame['4k']) return frameUrl(frame['4k']);
    if ((res === '4k' || res === 'hd') && frame.hd) return frameUrl(frame.hd);
    return frameUrl(frame.standard);
  }, [frameUrl]);

  // Keyboard
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (detailIndex !== null) { setDetailIndex(null); resetZoom(); }
        else onClose();
      }
      if (detailIndex !== null) {
        if (e.key === 'ArrowRight' && detailIndex < frames.length - 1) goToDetail(detailIndex + 1);
        if (e.key === 'ArrowLeft' && detailIndex > 0) goToDetail(detailIndex - 1);
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [detailIndex, frames.length, onClose]);

  // ── Touch handlers ──────────────────────────────────────────────────────────

  function onTouchStart(e: React.TouchEvent) {
    if (e.touches.length === 2) {
      // Start pinch
      pinching.current = true;
      pinchStartDist.current = getTouchDist(e.touches[0], e.touches[1]);
      pinchStartScale.current = scaleRef.current;
      pinchMidX.current = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      pinchMidY.current = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      panStartTx.current = txRef.current;
      panStartTy.current = tyRef.current;
    } else if (e.touches.length === 1) {
      pinching.current = false;
      panStartTouchX.current = e.touches[0].clientX;
      panStartTouchY.current = e.touches[0].clientY;
      panStartTx.current = txRef.current;
      panStartTy.current = tyRef.current;

      // Double-tap detection
      const now = Date.now();
      if (now - lastTapTime.current < 250) {
        // double tap
        if (scaleRef.current > 1) {
          resetZoom();
        } else {
          applyTransform(2.5, 0, 0);
        }
        lastTapTime.current = 0;
      } else {
        lastTapTime.current = now;
      }
    }
  }

  function onTouchMove(e: React.TouchEvent) {
    if (e.touches.length === 2 && pinching.current) {
      e.preventDefault();
      const dist = getTouchDist(e.touches[0], e.touches[1]);
      const newScale = Math.min(8, Math.max(1, pinchStartScale.current * (dist / pinchStartDist.current)));
      // keep pinch midpoint fixed visually
      const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      const dMidX = midX - pinchMidX.current;
      const dMidY = midY - pinchMidY.current;
      applyTransform(newScale, panStartTx.current + dMidX, panStartTy.current + dMidY);
    } else if (e.touches.length === 1 && !pinching.current && scaleRef.current > 1) {
      e.preventDefault();
      const dx = e.touches[0].clientX - panStartTouchX.current;
      const dy = e.touches[0].clientY - panStartTouchY.current;
      applyTransform(scaleRef.current, panStartTx.current + dx, panStartTy.current + dy);
    }
  }

  function onTouchEnd(e: React.TouchEvent) {
    if (e.touches.length === 1 && pinching.current) {
      // Pinch → pan transition: capture new pan origin so there's no jump
      pinching.current = false;
      panStartTouchX.current = e.touches[0].clientX;
      panStartTouchY.current = e.touches[0].clientY;
      panStartTx.current = txRef.current;
      panStartTy.current = tyRef.current;
    } else if (e.touches.length < 2) {
      pinching.current = false;
    }
    if (scaleRef.current <= 1) resetZoom();
  }

  // ───────────────────────────────────────────────────────────────────────────

  const activeFrame = detailIndex !== null ? frames[detailIndex] : null;

  return (
    <div className="frame-gallery-overlay">
      <div className="frame-gallery-header">
        <span className="frame-gallery-counter">
          {detailIndex !== null
            ? `${detailIndex + 1} / ${frames.length}`
            : `${sectionName} — ${frames.length} frames`}
        </span>
        <button
          className="frame-gallery-close"
          onClick={() => {
            if (detailIndex !== null) { setDetailIndex(null); resetZoom(); }
            else onClose();
          }}
        >
          ✕
        </button>
      </div>

      {loading ? (
        <div className="frame-gallery-loading">Loading frames…</div>
      ) : frames.length === 0 ? (
        <div className="frame-gallery-loading">No frames found</div>
      ) : detailIndex !== null && activeFrame ? (
        /* ── Detail mode ── */
        <div className="frame-gallery-detail">

          {/* Resolution pills */}
          <div className="frame-gallery-pills">
            {(['standard', 'hd', '4k'] as Resolution[]).map((r) => {
              const available = r === 'standard' ? !!activeFrame.standard
                : r === 'hd' ? !!activeFrame.hd
                : !!activeFrame['4k'];
              return (
                <button
                  key={r}
                  className={`frame-gallery-pill${resolution === r ? ' active' : ''}`}
                  disabled={!available}
                  onClick={() => { setResolution(r); resetZoom(); }}
                >
                  {r === 'standard' ? 'Standard' : r === 'hd' ? 'HD' : '4K'}
                </button>
              );
            })}
          </div>

          {/* Touch area — fills remaining space */}
          <div
            className="frame-gallery-zoom-area"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <img
              src={resolvedUrl(activeFrame, resolution)}
              alt={`Frame ${activeFrame.index}`}
              className="frame-gallery-detail-img"
              draggable={false}
              style={{
                transform: `translate(${transform.tx}px, ${transform.ty}px) scale(${transform.scale})`,
                transformOrigin: 'center center',
                cursor: transform.scale > 1 ? 'grab' : 'zoom-in',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                touchAction: 'none',
              }}
            />
          </div>

          {transform.scale > 1 && (
            <div className="frame-gallery-zoom-hint">Double-tap to reset • Pinch to zoom • Drag to pan</div>
          )}

          {/* Prev / Next nav */}
          <div className="frame-gallery-nav">
            <button
              className="frame-gallery-nav-btn"
              disabled={detailIndex === 0}
              onClick={() => goToDetail(detailIndex - 1)}
            >
              ← Prev
            </button>
            <button
              className="frame-gallery-nav-btn"
              disabled={detailIndex === frames.length - 1}
              onClick={() => goToDetail(detailIndex + 1)}
            >
              Next →
            </button>
          </div>
        </div>

      ) : (
        /* ── Gallery scroll mode ── */
        <div className="frame-gallery-scroll">
          {frames.map((frame, i) => (
            <button
              key={frame.index}
              className="frame-gallery-thumb-btn"
              onClick={() => goToDetail(i)}
            >
              <img
                src={frameUrl(frame.standard)}
                alt={`Frame ${frame.index}`}
                className="frame-gallery-thumb"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
